import React, { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Mail, Phone, MapPin, MessageSquare, Calendar, CheckCircle2, AlertCircle } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { MagneticButton } from "../components/MagneticButton";
import { postJSON } from "../lib/api";
import { SERVICES } from "../lib/services";
import { usePageMeta } from "../lib/usePageMeta";
import { SEO } from "../components/SEO";

function cleanAddress(raw) {
    if (!raw) return "";
    return raw.replace(/(\bSte\s+[A-Z])([A-Z][a-z])/g, "$1, $2");
}

const INTERESTS = SERVICES.map((s) => s.title);

export const Contact = () => {
    const { company } = useOutletContext() || {};
    const navigate = useNavigate();
    const contacts = company?.contacts || {};
    const email = contacts.email || "info@cybersecurityforefront.com";
    const phone = contacts.phone || "";
    const address = cleanAddress(contacts.address || "");

    usePageMeta({
        title: "Contact — Talk to a real cybersecurity engineer",
        description:
            "We respond within one business day. Reach Cybersecurity Forefront by email, phone, or book a consultation through the AI concierge.",
    });

    const [form, setForm] = useState({
        name: "",
        emailAddr: "",
        company: "",
        message: "",
        interests: [],
    });
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState("");

    const toggleInterest = (label) => {
        setForm((f) =>
            f.interests.includes(label)
                ? { ...f, interests: f.interests.filter((x) => x !== label) }
                : { ...f, interests: [...f.interests, label] },
        );
    };

    const submit = async (e) => {
        e.preventDefault();
        if (status === "loading") return;
        setStatus("loading");
        setError("");
        try {
            await postJSON("/contact", {
                name: form.name.trim(),
                email: form.emailAddr.trim(),
                company: form.company.trim(),
                message: form.message.trim(),
                interests: form.interests,
            });
            setStatus("success");
        } catch (err) {
            setStatus("error");
            setError(err.message || "Could not submit. Please try again.");
        }
    };

    const goBookConsultation = () =>
        navigate("/", { state: { chatMessage: "I'd like to book a consultation." } });

    return (
        <div className="pt-24 md:pt-28 pb-6 mx-auto max-w-7xl px-5 md:px-10" data-testid="contact-page">
            <SEO
                title="Contact — Talk to senior security engineers"
                description="Reach Cybersecurity Forefront for partnerships, MSP onboarding, security incidents, or product questions. Wyoming LLC, USA — replies within one business day."
                canonicalPath="/contact"
            />
            <Reveal>
                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                    Contact
                </p>
                <h1
                    className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tight leading-[1.05] max-w-3xl"
                    data-testid="contact-title"
                >
                    Talk to a real engineer.
                </h1>
                <p className="mt-5 text-text-muted max-w-2xl leading-relaxed">
                    We respond within one business day. For urgent incidents, call the
                    on-call line directly.
                </p>
            </Reveal>

            <div className="mt-12 grid gap-8 lg:grid-cols-12">
                {/* Left: form */}
                <div className="lg:col-span-7">
                    {status === "success" ? (
                        <div
                            className="rounded-2xl border border-accent-mint/30 bg-accent-mint/[0.04] p-8"
                            data-testid="contact-success"
                        >
                            <CheckCircle2 className="w-7 h-7 text-accent-mint" />
                            <h3 className="mt-4 text-xl font-semibold text-text-primary">
                                Got it.
                            </h3>
                            <p className="mt-2 text-text-muted leading-relaxed">
                                We&apos;ll get back to you within 24 hours. If something is
                                burning right now, call <span className="text-text-primary">{phone}</span>.
                            </p>
                        </div>
                    ) : (
                        <form
                            onSubmit={submit}
                            className="rounded-2xl border border-white/[0.06] bg-surface/60 backdrop-blur-md p-6 md:p-8 space-y-4"
                            data-testid="contact-form"
                        >
                            <div className="grid gap-4 sm:grid-cols-2">
                                <input
                                    type="text"
                                    required
                                    placeholder="Full name"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="bg-canvas/60 border border-white/[0.07] rounded-md px-3 py-2.5 text-[13.5px] text-text-primary placeholder:text-text-dim outline-none focus:border-accent-cyan/50 transition-colors duration-200"
                                    data-testid="contact-name"
                                />
                                <input
                                    type="email"
                                    required
                                    placeholder="Work email"
                                    value={form.emailAddr}
                                    onChange={(e) => setForm({ ...form, emailAddr: e.target.value })}
                                    className="bg-canvas/60 border border-white/[0.07] rounded-md px-3 py-2.5 text-[13.5px] text-text-primary placeholder:text-text-dim outline-none focus:border-accent-cyan/50 transition-colors duration-200"
                                    data-testid="contact-email"
                                />
                            </div>
                            <input
                                type="text"
                                placeholder="Company (optional)"
                                value={form.company}
                                onChange={(e) => setForm({ ...form, company: e.target.value })}
                                className="w-full bg-canvas/60 border border-white/[0.07] rounded-md px-3 py-2.5 text-[13.5px] text-text-primary placeholder:text-text-dim outline-none focus:border-accent-cyan/50 transition-colors duration-200"
                                data-testid="contact-company"
                            />
                            <textarea
                                required
                                rows={5}
                                placeholder="What can we help with?"
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                className="w-full bg-canvas/60 border border-white/[0.07] rounded-md px-3 py-2.5 text-[13.5px] text-text-primary placeholder:text-text-dim outline-none focus:border-accent-cyan/50 transition-colors duration-200 resize-y"
                                data-testid="contact-message"
                            />
                            <div>
                                <p className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-text-dim mb-2.5">
                                    Interested in
                                </p>
                                <div className="flex flex-wrap gap-2" data-testid="contact-interests">
                                    {INTERESTS.map((label) => {
                                        const active = form.interests.includes(label);
                                        return (
                                            <button
                                                key={label}
                                                type="button"
                                                onClick={() => toggleInterest(label)}
                                                className={`text-[12px] font-mono px-2.5 py-1 rounded-md border transition-colors duration-200 ${
                                                    active
                                                        ? "border-accent-cyan/50 bg-accent-cyan/[0.08] text-accent-cyan"
                                                        : "border-white/[0.07] bg-elevated/40 text-text-muted hover:text-text-primary"
                                                }`}
                                                data-testid={`contact-interest-${label.replace(/\W+/g, "-").toLowerCase()}`}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            {status === "error" && (
                                <div
                                    className="flex items-center gap-2 text-[12px] text-accent-red bg-accent-red/5 border border-accent-red/20 rounded-md px-3 py-2"
                                    data-testid="contact-error"
                                >
                                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                                    {error}
                                </div>
                            )}
                            <div className="flex justify-end">
                                <MagneticButton
                                    type="submit"
                                    disabled={status === "loading"}
                                    className="px-5 py-2.5 rounded-md bg-accent-cyan text-canvas text-[13px] font-medium hover:shadow-glow-cyan disabled:opacity-40 transition-shadow duration-200"
                                    data-testid="contact-submit"
                                    ariaLabel="Send message"
                                >
                                    {status === "loading" ? "Sending…" : "Send message"}
                                </MagneticButton>
                            </div>
                        </form>
                    )}
                </div>

                {/* Right: contact details + quick cards */}
                <div className="lg:col-span-5 space-y-5" data-testid="contact-details">
                    {address && (
                        <div className="rounded-2xl border border-white/[0.06] bg-surface/60 p-6">
                            <MapPin className="w-4 h-4 text-accent-cyan" />
                            <p className="mt-3 text-[10.5px] font-mono uppercase tracking-[0.22em] text-text-dim">
                                Office
                            </p>
                            <p className="mt-1.5 text-[14px] text-text-primary leading-relaxed" data-testid="contact-address">
                                {address}
                            </p>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <a
                            href={`mailto:${email}`}
                            className="rounded-xl border border-white/[0.06] bg-surface/40 p-4 hover:border-accent-cyan/40 hover:bg-accent-cyan/[0.04] transition-colors duration-200 group"
                            data-testid="contact-quick-email"
                        >
                            <Mail className="w-4 h-4 text-accent-cyan" />
                            <p className="mt-3 text-[11px] font-mono uppercase tracking-[0.18em] text-text-dim">
                                Email us
                            </p>
                            <p className="mt-1 text-[12.5px] text-text-primary truncate group-hover:text-accent-cyan transition-colors duration-200">
                                {email}
                            </p>
                        </a>
                        {phone && (
                            <a
                                href={`tel:${phone.replace(/\s+/g, "")}`}
                                className="rounded-xl border border-white/[0.06] bg-surface/40 p-4 hover:border-accent-cyan/40 hover:bg-accent-cyan/[0.04] transition-colors duration-200 group"
                                data-testid="contact-quick-phone"
                            >
                                <Phone className="w-4 h-4 text-accent-cyan" />
                                <p className="mt-3 text-[11px] font-mono uppercase tracking-[0.18em] text-text-dim">
                                    Call us
                                </p>
                                <p className="mt-1 text-[12.5px] text-text-primary group-hover:text-accent-cyan transition-colors duration-200">
                                    {phone}
                                </p>
                            </a>
                        )}
                        <button
                            type="button"
                            onClick={goBookConsultation}
                            className="text-left rounded-xl border border-white/[0.06] bg-surface/40 p-4 hover:border-accent-cyan/40 hover:bg-accent-cyan/[0.04] transition-colors duration-200 group"
                            data-testid="contact-quick-book"
                        >
                            <Calendar className="w-4 h-4 text-accent-cyan" />
                            <p className="mt-3 text-[11px] font-mono uppercase tracking-[0.18em] text-text-dim">
                                Book a call
                            </p>
                            <p className="mt-1 text-[12.5px] text-text-primary group-hover:text-accent-cyan transition-colors duration-200">
                                Via concierge →
                            </p>
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate("/", { state: { chatMessage: "Tell me about ZeroConf." } })}
                            className="text-left rounded-xl border border-white/[0.06] bg-surface/40 p-4 hover:border-accent-cyan/40 hover:bg-accent-cyan/[0.04] transition-colors duration-200 group"
                            data-testid="contact-quick-zeroconf"
                        >
                            <MessageSquare className="w-4 h-4 text-accent-cyan" />
                            <p className="mt-3 text-[11px] font-mono uppercase tracking-[0.18em] text-text-dim">
                                Ask AI
                            </p>
                            <p className="mt-1 text-[12.5px] text-text-primary group-hover:text-accent-cyan transition-colors duration-200">
                                Tell me about ZeroConf
                            </p>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Contact;
